import { z } from 'zod';
import { CHAT_PRESENTATION } from './questions.js';
/**
 * The chat transcript: what the core sends (conversation module, task 25) and the web renders (chat
 * screen, task 28). Pure definitions and rules only; docs/design/chat-ux.md explains them.
 *
 * The rules, in short:
 * - Tapping a quick reply or saving an inline form card is the person's own answer: it is saved at once.
 * - Anything read from typed words or a document is shown back on a confirmation card first.
 * - Mandatory and kill-switch answers need their own Confirm; optional ones can be confirmed together.
 * - A newer card, or a new message from the person, expires the rows still open on older cards.
 */
/** More quick replies than this and the question becomes an inline form card (rating scales allow 10). */
export const QUICK_REPLY_LIMIT = 8;
const RATING_REPLY_LIMIT = 10;
/** The most a person can type in one message (the agent service refuses longer ones). */
export const CHAT_MESSAGE_MAX = 2000;
/** The quick replies for a question, in configured order. Empty when it has none. */
export function quickReplies(q) {
    if (q.type === 'rating')
        return Array.from({ length: q.settings.scale }, (_, i) => ({
            value: String(i + 1),
            label: String(i + 1),
        }));
    return q.options.map((o) => ({ value: o.value, label: o.label }));
}
/** Several quick replies can be picked (then sent with Done) rather than one tap. */
export function isMultiSelect(q) {
    return (q.type === 'multiple_choice' ||
        q.type === 'asset_selector' ||
        (q.type === 'entity_selector' && q.settings.multiple));
}
/**
 * How a question is asked in chat: the type's default (CHAT_PRESENTATION), refined by its settings.
 * Selectors with a short list of options get quick replies; long lists get a form card instead.
 */
export function chatPresentation(q) {
    const base = CHAT_PRESENTATION[q.type];
    const replies = quickReplies(q).length;
    const limit = q.type === 'rating' ? RATING_REPLY_LIMIT : QUICK_REPLY_LIMIT;
    if ((q.type === 'entity_selector' || q.type === 'asset_selector') && replies > 0 && replies <= limit)
        return 'quick_replies';
    if (base === 'quick_replies' && replies > limit)
        return 'inline_form';
    return base;
}
/** The answer value for quick replies picked by tapping (checkAnswer it before sending). */
export function tapValue(q, picked) {
    if (q.type === 'rating')
        return Number(picked[0]);
    if (isMultiSelect(q)) {
        const set = new Set(picked);
        // Configured order, so the stored answer does not depend on tap order.
        return quickReplies(q)
            .map((r) => r.value)
            .filter((v) => set.has(v));
    }
    return picked[0];
}
/** Mandatory and kill-switch answers are confirmed one at a time, never as part of a combined card. */
export function needsOwnConfirmation(q) {
    return q.mandatory || q.metadata.kill_switch;
}
// ---------- transcript items ----------
const Id = z.string().min(1).max(100);
const QuestionKey = z.string().min(1).max(64);
const QuickReplySchema = z.strictObject({ value: z.string().min(1), label: z.string().min(1) });
export const CONFIRMATION_ROW_STATES = ['open', 'confirmed', 'declined', 'expired'];
export const ConfirmationRow = z.strictObject({
    question: QuestionKey,
    question_text: z.string().min(1),
    /** The value to save, already in the question's answer shape. */
    value: z.unknown(),
    /** How the value reads to a person, e.g. "Microsoft 365" or "RM 4,200,000". */
    label: z.string().min(1),
    confidence: z.number().min(0).max(1),
    /** The person's words, or the document's, that support the value. */
    quote: z.string().max(1000).nullable().default(null),
    page: z.number().int().positive().nullable().default(null),
    own_confirmation: z.boolean(),
    kill_switch: z.boolean().default(false),
    state: z.enum(CONFIRMATION_ROW_STATES).default('open'),
});
export const ChatItem = z.discriminatedUnion('kind', [
    /** The assistant speaking: feedback, help, or a clarifying question. */
    z.strictObject({
        kind: z.literal('bot'),
        id: Id,
        text: z.string().min(1),
        /** Points the last saved answer moved the score by, once scoring exists (task 22). */
        score_delta: z.number().nullable().default(null),
        /** What an explanation was drawn from: control library and glossary passages (decision 0013). */
        sources: z
            .array(z.strictObject({ id: z.string(), title: z.string() }))
            .max(5)
            .default([]),
    }),
    /** What the person sent. "tapped": a quick reply; "form": an inline form card. */
    z.strictObject({
        kind: z.literal('user'),
        id: Id,
        text: z.string().min(1),
        via: z.enum(['typed', 'tapped', 'form']),
    }),
    /** A question being asked. Only the latest unanswered one is live (shows its quick replies or form). */
    z.strictObject({
        kind: z.literal('question'),
        id: Id,
        question: QuestionKey,
        stage: z.strictObject({ key: z.string(), name: z.string() }).nullable().default(null),
        /** The question in conversational words; falls back to the configured text. */
        text: z.string().min(1),
        /** Why it is asked, in one line. */
        why: z.string().default(''),
        presentation: z.enum(['quick_replies', 'typed', 'date_picker', 'upload', 'inline_form']),
        quick_replies: z.array(QuickReplySchema).default([]),
        multiple: z.boolean().default(false),
        kill_switch: z.boolean().default(false),
        optional: z.boolean().default(false),
        /** Asked again after the person changed or declined an answer. */
        again: z.boolean().default(false),
    }),
    /** What the assistant understood, shown back before anything is saved. */
    z.strictObject({
        kind: z.literal('confirmation'),
        id: Id,
        /** The chat turn the rows were read from, kept as evidence on each saved answer. */
        turn_id: Id,
        source: z.discriminatedUnion('kind', [
            z.strictObject({ kind: z.literal('chat') }),
            z.strictObject({ kind: z.literal('document'), document_id: Id, name: z.string().min(1) }),
        ]),
        rows: z.array(ConfirmationRow).min(1).max(10),
    }),
    /** A stage finished. */
    z.strictObject({
        kind: z.literal('stage'),
        id: Id,
        stage: z.string(),
        name: z.string(),
        message: z.string().default(''),
    }),
    /** Nothing more to ask right now. */
    z.strictObject({
        kind: z.literal('done'),
        id: Id,
        answered: z.number().int().min(0),
        required_left: z.number().int().min(0),
    }),
]);
// ---------- confirmation cards ----------
/** Open while any row is open. */
export function cardIsOpen(card) {
    return card.rows.some((r) => r.state === 'open');
}
/**
 * Which open rows the footer's Confirm saves, and which need their own button. A one-row card confirms
 * with the footer button, which counts as its own confirmation. On longer cards, buttons stay on their
 * rows until used, so nothing moves under the person's finger.
 */
export function confirmationActions(card) {
    const open = card.rows.filter((r) => r.state === 'open');
    if (card.rows.length === 1)
        return { own: [], together: open.map((r) => r.question) };
    return {
        own: open.filter((r) => r.own_confirmation).map((r) => r.question),
        together: open.filter((r) => !r.own_confirmation).map((r) => r.question),
    };
}
/**
 * The answers to submit (channel "chat") when the person confirms these rows. `confirmation` is "button"
 * for a row confirmed on its own and "card" for rows confirmed together, as the answer engine requires.
 */
export function confirmedAnswers(card, questions) {
    const rows = card.rows.filter((r) => r.state === 'open' && questions.includes(r.question));
    const confirmation = rows.length === 1 ? 'button' : 'card';
    return rows.map((r) => {
        if (confirmation === 'card' && r.own_confirmation)
            throw new Error(`${r.question} needs its own confirmation, not a combined one`);
        const evidence = card.source.kind === 'document'
            ? {
                kind: 'document',
                document_id: card.source.document_id,
                ...(r.page ? { page: r.page } : {}),
                ...(r.quote ? { quote: r.quote } : {}),
            }
            : { kind: 'chat_message', turn_id: card.turn_id, ...(r.quote ? { quote: r.quote } : {}) };
        return {
            question: r.question,
            value: r.value,
            confirmation,
            evidence: [evidence],
            confidence: r.confidence,
        };
    });
}
/** The card after some of its open rows were confirmed or declined. */
export function resolveRows(card, questions, to) {
    return {
        ...card,
        rows: card.rows.map((r) => r.state === 'open' && questions.includes(r.question) ? { ...r, state: to } : r),
    };
}
/** Expires every row still open, on every card: done when a newer card arrives or the person types again. */
export function expireOpenRows(items) {
    return items.map((it) => it.kind === 'confirmation' && cardIsOpen(it)
        ? { ...it, rows: it.rows.map((r) => (r.state === 'open' ? { ...r, state: 'expired' } : r)) }
        : it);
}
/**
 * The question waiting for an answer: the latest one asked, unless a card holding a proposed answer is
 * still open after it, or the conversation has finished. Help replies in between keep it live.
 */
export function liveQuestion(items) {
    for (let i = items.length - 1; i >= 0; i--) {
        const it = items[i];
        if (it.kind === 'question')
            return it;
        if (it.kind === 'done' || (it.kind === 'confirmation' && cardIsOpen(it)))
            return null;
    }
    return null;
}
//# sourceMappingURL=chat.js.map